import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { ClaimedLinesOut, TransactionClaimsOut } from '../api/claimedLines';
import { listClaimedLines } from '../api/claimedLines';
import { useAuth } from '../auth/AuthContext';
import {
  chunkTransactionIds,
  dedupeTransactionIds,
} from './useTransactionDocuments';

/** Merge the per-chunk responses into one ext-transaction-id → claims
 *  map. Chunks never overlap, so last-write-wins is safe. */
export function mergeClaimPages(
  pages: ClaimedLinesOut[],
): Map<string, TransactionClaimsOut> {
  const merged = new Map<string, TransactionClaimsOut>();
  for (const page of pages) {
    for (const [transactionId, claims] of Object.entries(page.claims)) {
      merged.set(transactionId, claims);
    }
  }
  return merged;
}

/** Batched filed-claim lookup for a set of findings' transactions —
 *  the claimed-GST sibling of `useTransactionDocuments`, sharing its
 *  chunking so the id lists stay under the route's cap and proxy URL
 *  limits. `periodCount === 0` (the no-claim-data customer) is the
 *  signal to skip the claimed-GST surfaces entirely. Disabled without
 *  a customer slug: CLI-triggered runs have no customer context. */
export function useClaimedLines(
  customerSlug: string | null,
  transactionIds: string[],
): {
  claimsByTransaction: Map<string, TransactionClaimsOut>;
  claimPeriodCount: number;
  claimsReady: boolean;
} {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const ids = useMemo(() => dedupeTransactionIds(transactionIds), [transactionIds]);

  const query = useQuery({
    queryKey: ['claimed-lines', customerSlug, userKey, ids.join(',')],
    enabled: isSignedIn && customerSlug !== null && ids.length > 0,
    queryFn: async () => {
      const token = (await getIdToken()) ?? '';
      const slug = customerSlug ?? '';
      return Promise.all(
        chunkTransactionIds(ids).map((chunk) => listClaimedLines(slug, chunk, token)),
      );
    },
  });

  const claimsByTransaction = useMemo(
    () => (query.data ? mergeClaimPages(query.data) : new Map<string, TransactionClaimsOut>()),
    [query.data],
  );
  const claimPeriodCount = query.data?.[0]?.period_count ?? 0;

  return {
    claimsByTransaction,
    claimPeriodCount,
    // An errored lookup must not render as "nothing claimed" — callers
    // skip the panel until the data is really there.
    claimsReady: query.data !== undefined,
  };
}
